import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { BulkCreateEditSessionLogDto, EditSessionLogService } from '@connect-services/client/sdk/api';
import { EditState, EntityEdit, LayerSnapshot } from '@connect-services/client/services/edit-mode-state.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';

@Injectable({
    providedIn: 'root',
})
export class EditSessionLogBuilderService {
    private readonly editSessionLogService = inject(EditSessionLogService);
    private readonly projectEditModeService = inject(ProjectEditModeService);

    public async createEditSessionLogs(projectId: string, editState: EditState): Promise<void> {
        if (!projectId) {
            return;
        }

        const editSessionLogs = this.buildEditSessionLogs(editState);

        if (!editSessionLogs?.length) {
            return;
        }

        await firstValueFrom(
            this.editSessionLogService.editSessionLogControllerPost({
                bulkCreateEditSessionLogDto: {
                    logs: editSessionLogs,
                },
            }),
        );
    }

    public async endSession(tableNames?: string[]): Promise<void> {
        const currentSession = this.projectEditModeService.getEditSession();
        if (!currentSession?.id) {
            return;
        }

        await firstValueFrom(
            this.editSessionLogService.editSessionLogControllerDeleteByEditSessionId({
                editSessionId: currentSession.id,
                tableNames: tableNames,
            }),
        );
    }

    private buildEditSessionLogs(state: EditState): BulkCreateEditSessionLogDto['logs'] | null {
        const editSession = this.projectEditModeService.getEditSession();
        if (!editSession) {
            return null;
        }

        const startDate = new Date(editSession.startDate);

        return Object.values(state).flatMap((layerState: LayerSnapshot) => {
            return Object.entries(layerState).map(([entityId, entityState]: [string, EntityEdit]) => ({
                editSessionId: editSession.id,
                entityId: entityId,
                log: { ...entityState, properties: entityState.properties ?? {} },
                startedAt: startDate,
            }));
        });
    }
}
